import {
	Component,
	ComponentFactoryResolver,
	OnDestroy,
	OnInit,
} from "@angular/core";
import { MediasService } from "../../../../services/medias.service";
import { CategoryService } from "../../../../services/category.service";
import {
	EventModel,
	EventSubscriptionType,
	EventType,
} from "../../../../shared/interfaces/event.model";
import { DynamicHostModel } from "../../../../shared/modal/models/dynamic-host.model";
import { EditCreateMediaComponent } from "./edit-create-media/edit-create-media.component";
import { Subject } from "rxjs";
import { BsModalService } from "ngx-bootstrap/modal";
import { BroadcastService } from "../../../../shared/services/broadcast.service";
import { takeUntil } from "rxjs/operators";
import { NRModalService } from "../../../../shared/modal/services/nrmodal.service";
import { SwalService } from "../../../../shared/services/swal.service";
import { ShowVideoComponent } from "./show-video/show-video.component";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { ModalSize } from "../../../../shared/modal/models/modal.model";
import { ExportReportComponent } from "./export-report/export-report.component";
import { CustomersService } from "../../../../services/customers.service";
import { ControlService } from "../../../../services/control.service";
import { Router } from "@angular/router";

@Component({
	selector: "app-media",
	templateUrl: "./media.component.html",
	styleUrls: ["./media.component.scss"],
})
export class MediaComponent implements OnInit, OnDestroy {
	canAddMedia: boolean = false;
	validity: boolean = true;
	clientes: any[] = [];
	selectedClientId: number | null = null;
	medias: any;
	today = new Date();
	sortOptions: any;
	sortOrder: number;
	clientesMap = new Map<number, string>();
	sortField: string;
	searchName: string = "";
	isAdmin: boolean = false;
	categories: any[] = [];
	hoveredCard: number | null = null;
	selectedCategoryId: number | null = null;
	ref: DynamicDialogRef | undefined;
	paginationData: any = {
		current_page: 1,
		from: 1,
		last_page: 0,
		per_page: 0,
		to: 0,
		total: 0,
	};

	currentMonth = new Date().toLocaleString("es-MX", { month: "long" });
	private atComponentDestroy = new Subject();

	constructor(
		private mediaService: MediasService,
		public modalRef: BsModalService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private broadcast: BroadcastService,
		private nrModal: NRModalService,
		private swal: SwalService,
		public dialogService: DialogService,
		private categoryService: CategoryService,
		private customersService: CustomersService,
		private router: Router,
		private controlservice: ControlService,
	) {}

	ngOnDestroy(): void {
		this.atComponentDestroy.next();
		this.atComponentDestroy.unsubscribe();
	}

	async ngOnInit(): Promise<void> {
		this.controlservice.isAdmin().subscribe((res) => {
			this.isAdmin = res.is_admin;
		});
		this.customersService.getLimits().then((res: any) => {
			const mediaLimit = res.media || { used: 0, limit: 0 };
			this.canAddMedia = mediaLimit.used < mediaLimit.limit;
		});
		this.clientes = await this.customersService.getActives();

		this.clientes.forEach((cliente) => {
			this.clientesMap.set(
				cliente.id,
				cliente.nombre_comercial ?? cliente.name,
			);
		});
		const filters: any = {};
		filters.validity = this.validity;
		this.updateDataTable(1, filters);

		this.broadcast
			.onEvent(EventSubscriptionType.TYPE, EventType.MODAL)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: EventModel) => {
				const props = this.prepareProps(event);
				if (props) {
					this.nrModal.open(props);
				}
			});

		this.broadcast
			.onEvent(EventSubscriptionType.TYPE, EventType.DATATABLE)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: EventModel) => {
				this.updateDataTable(1, { validity: this.validity });
			});

		this.sortOptions = [
			{ label: "Price High to Low", value: "!price" },
			{ label: "Price Low to High", value: "price" },
		];
		this.categoryService.datatable().subscribe((res: any) => {
			this.categories = res.data || res;
		});
	}
	isCurrent(media): boolean {
		return new Date(media.vigencia) >= this.today;
	}

	getClientName(clientId: number | null): string {
		if (!clientId) return "Cliente no asociado";
		return this.clientesMap.get(clientId) || "Cliente no asociado";
	}
	applyFilters(): void {
		const filters: any = {};

		if (this.selectedCategoryId) {
			filters.category_id = this.selectedCategoryId;
		}
		if (this.selectedClientId) {
			filters.client_id = this.selectedClientId;
		}
		if (this.searchName?.trim()) {
			filters.key = this.searchName.trim();
		}
		filters.validity = this.validity;
		this.updateDataTable(1, filters);
	}
	getExtraCategoriesTooltip(categories: any[]): string {
		if (!categories || categories.length <= 1) return "";
		return categories
			.slice(2)
			.map((c) => c.name)
			.join(", ");
	}
	getDynamicCardStyle(categoryCount: number): { [key: string]: string } {
		const baseHeight = 450;
		const extraHeight = Math.ceil(categoryCount / 2) * 300;
		const totalHeight = baseHeight + (categoryCount > 1 ? extraHeight : 0);
		return {
			"max-height": `${totalHeight}px`,
		};
	}
	formatDate(dateString: string): string {
		if (!dateString) return "";
		const date = new Date(dateString);
		return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
	}
	clearFilters(): void {
		this.selectedCategoryId = null;
		this.selectedClientId = null;
		this.searchName = "";
		this.updateDataTable(1, { validity: this.validity });
	}
	toggleExpand(mediaId: number): void {
		if (this.hoveredCard === mediaId) {
			this.hoveredCard = null;
		} else {
			this.hoveredCard = mediaId;
		}
	}

	updateDataTable(page: number, options: any = {}): void {
		this.mediaService.datatable(page, options).subscribe((data: any) => {
			this.medias = data.data;
			delete data.data;
			delete data.links;
			this.paginationData = data;
		});
	}
	forceReload(): void {
		this.selectedCategoryId = null;
		this.updateDataTable(1);
	}
	/**
	 * Update fields on sort column update
	 * @param event
	 */
	onSortChange(event) {
		const value = event.value;

		if (value.indexOf("!") === 0) {
			this.sortOrder = -1;
			this.sortField = value.substring(1, value.length);
		} else {
			this.sortOrder = 1;
			this.sortField = value;
		}
	}

	/**
	 * Prepare data for th modal window
	 *
	 * @param event
	 * @returns
	 */
	prepareProps(event: EventModel): any {
		const props: DynamicHostModel = new DynamicHostModel();
		props.instantiated = false;
		props.modalModel = {
			component: null,
			componentFactoryResolver: this.componentFactoryResolver,
			data: event.data || {},
		};

		switch (event.name) {
			case "media.add":
				props.modalModel.component = EditCreateMediaComponent;
				props.modalModel.modalConfiguration = {
					title: "Agregar Media",
				};
				break;

			case "media.edit":
				props.modalModel.component = EditCreateMediaComponent;
				props.modalModel.modalConfiguration = {
					title: "Editar Media",
				};
				break;

			case "media.export":
				props.modalModel.component = ExportReportComponent;
				props.modalModel.modalConfiguration = {
					title: "Exportar Reporte",
					modalSize: ModalSize.MD,
				};
				break;

			case "media.delete":
				this.deleteMedia(event.data);
				return false;
		}

		return props;
	}

	/**
	 * Get confirmation from the user for deleting and send the signal to the server
	 * @param data
	 */
	deleteMedia(data): void {
		const swalParams = {
			title: "¿Estás seguro de querer eliminar el recurso?",
		};

		this.swal.warning(swalParams).then((result) => {
			if (result.value) {
				this.mediaService.delete(data.id).subscribe((resp: any) => {
					if (resp.success) {
						this.swal
							.success({ title: "Media eliminado exitosamente" })
							.then(() => {
								this.modalRef.hide(0);
								this.broadcast.fire({
									name: "reloadTable",
									type: EventType.DATATABLE,
									data: null,
								});
							});
					} else {
						this.swal.error(
							"Ocurrio un error al intentar borrar la publicidad",
						);
					}
				});
			}
		});
	}

	/**
	 * Open the modal window to export the xls file
	 * @param data
	 */
	exportMedia(data): void {
		this.broadcast.fire({
			name: "media.export",
			type: EventType.MODAL,
			data: {
				item: data,
			},
		});
	}
	editMedia(data): void {
		this.broadcast.fire({
			name: "media.edit",
			type: EventType.MODAL,
			data: {
				IsAdmin: this.isAdmin,
				item: data,
			},
		});
	}

	/**
	 * Open modal window
	 */
	openModal(): void {
		this.broadcast.fire({
			name: "media.add",
			type: EventType.MODAL,
			data: { IsAdmin: this.isAdmin, item: {} },
		});
	}

	makeKpi(): void {
		this.router.navigate(["admin/svs/report-kpi"]);
	}

	/**
	 * Open a modal window with the video
	 * @param media
	 */
	openVideo(media): void {
		this.ref = this.dialogService.open(ShowVideoComponent, {
			header: "Ver Video",
			width: "70%",
			closeOnEscape: false,
			baseZIndex: 10000,
			data: media,
		});
	}

	/**
	 * Alias function to refresh the medias table
	 *
	 * @param event
	 */
	getMedias(event): void {
		const filters: any = {};

		if (this.selectedCategoryId) {
			filters.category_id = this.selectedCategoryId;
		}
		if (this.selectedClientId) {
			filters.client_id = this.selectedClientId;
		}
		if (this.searchName?.trim()) {
			filters.key = this.searchName.trim();
		}
		filters.validity = this.validity;
		this.updateDataTable(event.page + 1, filters);
	}

	/**
	 * Get the medias data for pagination
	 * @param page
	 */
}
